import { z } from 'zod'

import { RuleID } from '@forto-dev/insights-engine-models'

import { TRANSPORT_TYPES, TransportType } from '@/features/shipments'

import { NOTIFICATION_PREFERENCE_TYPES } from './settings'

export const NOTIFICATION_STATUSES = ['new', 'unread', 'read'] as const
export const NotificationStatusEnum = z.enum(NOTIFICATION_STATUSES)

export type NotificationStatus = z.infer<typeof NotificationStatusEnum>

export const NOTIFICATION_TYPES = ['message', ...NOTIFICATION_PREFERENCE_TYPES] as const
export const NotificationTypeEnum = z.enum(NOTIFICATION_TYPES)
export type NotificationType = z.infer<typeof NotificationTypeEnum>

export type NotificationCounterParty = 'shipper' | 'consignee'

export type APINotification = {
  id: string
  status: NotificationStatus
  createdAt: string
  customerReference?: string
  transportType?: TransportType
  incoterm?: string
  shipperName?: string
  consignee?: string
  counterParty?: NotificationCounterParty
  type: NotificationType
  title: string
}

export type APINotificationResponse = {
  results: APINotification[]
  total: number
  page: number
  pageSize: number
}

export type APINotificationPreview = {
  id: string
  reference: RuleID
  renderedContent: string
}

export const NotificationPreviewSchema = z.object({
  id: z.string(),
  reference: z.nativeEnum(RuleID).or(z.string()),
  renderedContent: z.string(),
})

export type NotificationPreview = z.infer<typeof NotificationPreviewSchema>

export const NotificationSchema = z.object({
  id: z.string(),
  status: NotificationStatusEnum,
  createdAt: z.coerce.date(),
  customerReference: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  transportType: z
    .enum(TRANSPORT_TYPES)
    .or(z.string())
    .nullish()
    .transform((data) => data || undefined),
  incoterm: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  shipperName: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  consignee: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  counterParty: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  type: NotificationTypeEnum,
  title: z.string(),
})
export type Notification = z.infer<typeof NotificationSchema>

export const NotificationsResponseSchema = z.object({
  results: z.array(NotificationSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
})

export type NotificationsResponse = z.infer<typeof NotificationsResponseSchema>

export type APINotificationsCount = {
  new: number
  unread: number
}

export const NotificationCountSchema = z.object({
  new: z.number(),
  unread: z.number(),
})

export type NotificationCount = z.infer<typeof NotificationCountSchema>
